import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  numberSetService,
  type NumberSetItem as ApiNumberSetItem,
} from '../services/numberSetService'

interface NumberSetRow {
  id: number
  prizeTypeCode: string
  typeLabel: string
  number: string
}

// ใช้ prizeTypeCode ให้ตรงกับ backend และ LotteryBetSideRight / LotteryBetSideLeft (ตารางรางวัล + ส่งโพย)
// เรียงลำดับให้สอดคล้องกับ betTypeDisplayOrder / ตารางอัตราจ่าย
const playTypeOptions = [
  { value: '', label: '-- เลือกชนิด --' },
  { value: '2_DIGIT_TOP', label: '2 ตัวบน' },
  { value: '2_DIGIT_BOTTOM', label: '2 ตัวล่าง' },
  { value: '2_DIGIT_TOD', label: '2 ตัวโต๊ด' },
  { value: '2_DIGIT_REVERSE', label: '2 ตัวกลับ' },
  { value: '3_DIGIT_TOP', label: '3 ตัวบน' },
  { value: '3_DIGIT_BOTTOM', label: '3 ตัวล่าง' },
  { value: '3_DIGIT_TOD', label: '3 ตัวโต๊ด' },
  { value: '3_DIGIT_REVERSE', label: '3 ตัวบน (กลับเลข)' },
  { value: 'RUNNING_TOP', label: 'วิ่งบน' },
  { value: 'RUNNING_BOTTOM', label: 'วิ่งล่าง' },
  { value: '4_DIGIT_TOP', label: '4 ตัวบน' },
  { value: '4_DIGIT_TOD', label: '4 ตัวโต๊ด' },
  { value: '5_DIGIT_TOD', label: '5 ตัวโต๊ด' },
  { value: 'FIXED_POSITION_ONES', label: 'ปักหลักหน่วย' },
  { value: 'FIXED_POSITION_TENS', label: 'ปักหลักสิบ' },
  { value: 'FIXED_POSITION_HUNDREDS', label: 'ปักหลักร้อย' },
]

// จำนวนหลักที่ต้องตรงกับชนิด (ใช้ตรวจก่อนเพิ่มและก่อนบันทึก)
function getExpectedDigitCount(prizeTypeCode: string): number {
  if (prizeTypeCode.startsWith('2_DIGIT')) return 2
  if (prizeTypeCode.startsWith('3_DIGIT')) return 3
  if (prizeTypeCode.startsWith('4_DIGIT')) return 4
  if (prizeTypeCode === '5_DIGIT_TOD') return 5
  if (prizeTypeCode.startsWith('RUNNING_') || prizeTypeCode.startsWith('FIXED_POSITION_')) return 1
  return 0
}

// แยกเลขจาก string (คั่นด้วย comma/ช่องว่าง) แล้วตรวจว่าหลักตรงหรือไม่
function validateNumbersForType(
  prizeTypeCode: string,
  numbersStr: string
): { valid: string[]; invalid: string[]; expectedLen: number } {
  const expectedLen = getExpectedDigitCount(prizeTypeCode)
  const rawList = numbersStr.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
  const valid: string[] = []
  const invalid: string[] = []
  rawList.forEach((num) => {
    if (num.length === expectedLen) valid.push(num)
    else invalid.push(num)
  })
  return { valid, invalid, expectedLen }
}

const NumberSetCreatePage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [numberSetName, setNumberSetName] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [numberInput, setNumberInput] = useState('')
  const [rows, setRows] = useState<NumberSetRow[]>([])

  // กรองให้ช่องเลขรับเฉพาะตัวเลข (0-9) จุลภาค (,) และช่องว่าง
  const sanitizeNumberInput = (raw: string) => raw.replace(/[^\d,\s]/g, '')

  const loadOne = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      const data = await numberSetService.get(id)
      if (data) {
        setNumberSetName(data.name)
        const nextRows: NumberSetRow[] = []
        let rowId = Date.now()
        ;(data.items || []).forEach((item) => {
          const nums = sanitizeNumberInput(String(item.numbers || '')).split(/[\s,]+/).filter(Boolean)
          nums.forEach((num) => {
            nextRows.push({
              id: rowId++,
              prizeTypeCode: item.prizeTypeCode,
              typeLabel: item.typeLabel,
              number: num,
            })
          })
        })
        setRows(nextRows)
      } else {
        toast.error('ไม่พบเลขชุดนี้', { position: 'top-center' })
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'โหลดไม่สำเร็จ'
      toast.error(msg, { position: 'top-center' })
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (isEdit) loadOne()
    else setNumberSetName('')
  }, [isEdit, loadOne])

  // เมื่อพิมพ์เลข: ครบหลักตามชนิดให้ใส่ , อัตโนมัติ | ถ้าพิมพ์ , เองก่อนครบหลักให้ toast
  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const sanitized = sanitizeNumberInput(raw)
    const expectedLen = selectedType ? getExpectedDigitCount(selectedType) : 0

    if (expectedLen > 0) {
      // ลูกค้าพิมพ์ , เอง — ตรวจว่าเลขก่อน , ครบหลักหรือไม่
      if (sanitized.endsWith(',')) {
        const segmentBeforeComma = (sanitized.slice(0, -1).split(/[\s,]+/).pop() || '').trim()
        if (segmentBeforeComma.length !== expectedLen) {
          toast.error(
            `กรุณาพิมพ์ให้ครบ ${expectedLen} หลักก่อนค่อยใส่ , (ตอนนี้ ${segmentBeforeComma.length} หลัก)`,
            { position: 'top-center', duration: 4000 }
          )
          setNumberInput(sanitized.slice(0, -1))
          return
        }
      } else {
        // พิมพ์ครบหลักของเลขตัวปัจจุบัน → ใส่ , อัตโนมัติ
        const parts = sanitized.split(/[\s,]+/).filter(Boolean)
        const lastPart = (parts[parts.length - 1] || '').trim()
        if (lastPart.length === expectedLen) {
          setNumberInput(sanitized.trimEnd() + ',')
          return
        }
      }
    }

    setNumberInput(sanitized)
  }

  const handleAddNumberSet = () => {
    const cleaned = sanitizeNumberInput(numberInput).trim()
    if (!selectedType || !cleaned) {
      toast.error(selectedType ? 'กรุณากรอกเลข' : 'กรุณาเลือกชนิด', { position: 'top-center' })
      return
    }

    const opt = playTypeOptions.find((o) => o.value === selectedType)
    const typeLabel = opt?.label || selectedType
    const { valid, invalid, expectedLen } = validateNumbersForType(selectedType, cleaned)

    if (invalid.length > 0) {
      toast.error(
        `ชนิด "${typeLabel}" ต้องเป็นเลข ${expectedLen} หลัก — เลขที่ผิด: ${invalid.join(', ')}`,
        { position: 'top-center', duration: 5000 }
      )
      return
    }

    const baseId = Date.now()
    setRows((prev) => [
      ...prev,
      ...valid.map((num, i) => ({
        id: baseId + i,
        prizeTypeCode: selectedType,
        typeLabel,
        number: num,
      })),
    ])
    setSelectedType('')
    setNumberInput('')
  }

  const handleRemoveItem = (itemId: number) => {
    setRows((prev) => prev.filter((item) => item.id !== itemId))
  }

  const handleClearAll = () => {
    setNumberSetName('')
    setRows([])
    setSelectedType('')
    setNumberInput('')
  }

  const handleDeleteSet = async () => {
    if (!id) return
    try {
      setSaving(true)
      await numberSetService.delete(id)
      toast.success('ลบเลขชุดเรียบร้อยแล้ว', { position: 'top-center' })
      navigate('/number-set')
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'ลบไม่สำเร็จ'
      toast.error(msg, { position: 'top-center' })
    } finally {
      setSaving(false)
    }
  }

  const rowsToApiItems = (items: NumberSetRow[]): ApiNumberSetItem[] => {
    const byKey = new Map<string, NumberSetRow[]>()
    items.forEach((item) => {
      if (!byKey.has(item.prizeTypeCode)) {
        byKey.set(item.prizeTypeCode, [])
      }
      byKey.get(item.prizeTypeCode)!.push(item)
    })
    return Array.from(byKey.values()).map((group) => ({
      prizeTypeCode: group[0].prizeTypeCode,
      typeLabel: group[0].typeLabel,
      numbers: group.map((item) => item.number).join(', '),
    }))
  }

  const handleSave = async () => {
    const name = numberSetName.trim()
    if (!name) {
      toast.error('กรุณาระบุชื่อเลขชุด', { position: 'top-center' })
      return
    }
    if (rows.length === 0) {
      toast.error('กรุณาเพิ่มรายการในตารางอย่างน้อย 1 รายการ', { position: 'top-center' })
      return
    }

    // ตรวจความยาวเลขทุกรายการก่อนบันทึก
    const invalidRow = rows.find((row) => row.number.length !== getExpectedDigitCount(row.prizeTypeCode))
    if (invalidRow) {
      toast.error(
        `"${invalidRow.typeLabel}" ต้องเป็นเลข ${getExpectedDigitCount(invalidRow.prizeTypeCode)} หลัก — เลขที่ผิด: ${invalidRow.number}`,
        { position: 'top-center', duration: 6000 }
      )
      return
    }

    try {
      setSaving(true)
      if (isEdit && id) {
        await numberSetService.update(id, {
          name,
          items: rowsToApiItems(rows),
        })
      } else {
        await numberSetService.create({
          name,
          items: rowsToApiItems(rows),
        })
      }
      toast.success('บันทึกเลขชุดเรียบร้อยแล้ว', { position: 'top-center' })
      navigate('/number-set')
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'
      toast.error(msg, { position: 'top-center' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 px-1 text-center py-5">
              <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
              <p className="c-white mt-2">กำลังโหลด...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={() => navigate('/number-set')}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      จัดการเลขชุด
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-sort-numeric-up-alt me-2"></i>
                      {isEdit ? 'แก้ไขเลขชุด' : 'สร้างเลขชุด'}
                    </div>

                    <div className="bdr-5 bgc-4 c-dark p-2 mb-2">
                      <div className="row mx--1">
                        <div className="col-12 px-1 mb-2">
                          <div className="wrap t-medium">
                            <i className="fas fa-edit me-2"></i>
                            ชื่อเลขชุด
                          </div>
                        </div>

                        <div className="col-12 px-1 mb-2">
                          <input
                            type="text"
                            id="nameset"
                            className="w-100 border-0 bgc-7 bdr-5 p-2"
                            value={numberSetName}
                            onChange={(e) => setNumberSetName(e.target.value)}
                            placeholder="ชื่อเลขชุด"
                          />
                        </div>

                        <div className="col-12 px-1 mb-2">
                          <div className="wrap t-medium">
                            <i className="fas fa-plus me-2"></i>
                            สร้างชุดตัวเลข
                          </div>
                        </div>

                        <div className="col-12 px-1 mb-2">
                          <div className="table-responsive w-100">
                            <table className="table table-set">
                              <tbody>
                                {rows.map((item, index) => (
                                  <tr key={item.id}>
                                    <th scope="row" className="text-center">
                                      {index + 1}.
                                    </th>
                                    <td>
                                      <p className="bgc-7 c-dark bdr-5 p-2">
                                        {item.typeLabel}
                                      </p>
                                    </td>
                                    <td>
                                      <p className="bgc-7 c-dark bdr-5 p-2">
                                        {item.number}
                                      </p>
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="bgg-1 bdr-5 py-2 c-white w-100 border-0"
                                        onClick={() => handleRemoveItem(item.id)}
                                      >
                                        <p className="">
                                          <i className="fas fa-trash-alt"></i>
                                        </p>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="col-12 px-1">
                          <div className="row mx--1 align-items-center">
                            <div className="col-12 col-md-2 px-1 mb-2">
                              <h6 className="text-center t-medium">เลขชุด</h6>
                            </div>
                            <div className="col-6 col-md-4 px-1 mb-2">
                              <div className="bgc-7 px-2 py-1 bdr-5">
                                <select
                                  name="playtype"
                                  required
                                  className="w-100 border-0 bgc-7"
                                  value={selectedType}
                                  onChange={(e) => setSelectedType(e.target.value)}
                                >
                                  {playTypeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-6 col-md-6 px-1 mb-2">
                              <div className="bgc-7 p-1 bdr-5">
                                <input
                                  type="text"
                                  id="set"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  className="w-100 border-0 bgc-7 px-2 bdr-5"
                                  placeholder={
                                    selectedType
                                      ? `เลข ${getExpectedDigitCount(selectedType)} หลัก คั่นด้วย ,`
                                      : 'เลข เช่น 11,22,33,44,55'
                                  }
                                  value={numberInput}
                                  onChange={handleNumberInputChange}
                                />
                              </div>
                            </div>
                            <div className="col-12 px-1">
                              <button
                                type="button"
                                className="check-button bgg-1 bdc-1 c-white w-100"
                                onClick={handleAddNumberSet}
                              >
                                <p className="">
                                  <i className="fas fa-plus me-2"></i>
                                  เพิ่มชุดตัวเลข
                                </p>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bdr-5 bgc-4 c-dark p-2">
                      <div className="row mx--1">
                        <div className="col-6 px-1">
                          {isEdit ? (
                            <button
                              type="button"
                              className="check-button bgc-3 c-white w-100 border-0"
                              onClick={handleDeleteSet}
                              disabled={saving}
                            >
                              <p className="">
                                <i className="fas fa-trash-alt me-2"></i>
                                ลบเลขชุดทิ้ง
                              </p>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="check-button bgc-3 c-white w-100 border-0"
                                onClick={handleClearAll}
                              disabled={saving}
                            >
                              <p className="">
                                <i className="fas fa-eraser me-2"></i>
                                ล้างทั้งหมด
                              </p>
                            </button>
                          )}
                        </div>

                        <div className="col-6 px-1">
                          <button
                            type="button"
                            className="check-button bgg-4 c-white w-100 border-0"
                            onClick={handleSave}
                            disabled={saving}
                          >
                            <p className="">
                              {saving ? (
                                <i className="fas fa-spinner fa-spin me-2"></i>
                              ) : (
                                <i className="fas fa-hand-pointer me-2"></i>
                              )}
                              บันทึกข้อมูล
                            </p>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NumberSetCreatePage
